import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import TableStore from "@harbor/elements/utils/table/store";
import useSelectableRows from "@harbor/elements/utils/table/hooks/selectableRows";
import { applicationsPayload } from "../../../../../../../../config/reqConfig/anomalyDetection";
import {
  payloadOnQueryUpdate,
  defaultRowID,
  generateRowID,
} from "../../../../common/adUtils";
import { addUnitsFromDate } from "../../../../../../../../utils/displayTime";
import ApiService from "../../../../../../../../config/api-config";
import columnConfig from "./appTableConfig";
import { downloadAsCSVFile } from "../../../../../../../../utils/common";
import {
  AD_USAGE_APPLICATION_TABLE_DEFAULT_TGL,
  LOCAL_STORAGE_LABELS,
} from "../../../../../../../../utils/enums";
import { useTableSettings } from "../../../../../../../../utils/table";
import CustomTimeStamp from "../../../../../../../../generics/TimeStamp/customTimeStamp";
import ADTableView from "../../../../genericComponents/ADTableView";
import useI18n from "../../../../../../../../i18n/useI18n";
import css from "../../../../common/anomalyMagneticStyle.less";
import ErrorComponent from "../../../../../../../../common/ErrorComponent";
import Spinner from "../../../../../../../../common/Spinner";

const defaultSort = [["usage", "desc"]];
const tableSettingsId = "ad_usage_applications_table";
const ApplicationsTable = (props) => {
  const { tViews } = useI18n("views");
  const latestRequest = useRef(0);
  const tableStoreRef = useRef(null);
  const [tableLoader, setTableLoader] = useState(null);
  const [tableStore, setTableStore] = useState(null);
  const [tableError, setTableError] = useState(null);
  const [resReceived, setResReceived] = useState(null);

  const openTableSettings = useCallback(() => {
    tableStoreRef.current?.setState({ settingsOpen: true });
  }, []);

  const columnsHook = useColumns(
    useMemo(
      () => ({
        config: ({ store }) => columnConfig({ store, openTableSettings }),
        order: defaultSort,
      }),
      [openTableSettings],
    ),
  );

  const selectableRowsHook = useSelectableRows(
    useMemo(
      () => ({
        selected: defaultRowID(),
      }),
      [],
    ),
  );

  const getSelectedRows = useCallback((rows, source) => {
    const applicationNames = source
      .filter((item) => rows.includes(item.id))
      .map((item) => item.application);
    props.setAppUsgList(applicationNames);
  }, []);

  useEffect(() => {
    const runEffect = async () => {
      const abortController = new AbortController();
      latestRequest.current += 1;
      const requestId = latestRequest.current;
      if (props.circuit_name) {
        try {
          setResReceived(false);
          let tableStore = new TableStore({
            query: { pageLength: 30 },
            hooks: [columnsHook, selectableRowsHook],
            fetchData: async ({ query, setSource, setCount }) => {
              if (!query.export) setTableLoader(true);
              let payload = applicationsPayload(
                props.circuit_name,
                props.selectedHour,
              );
              payload = payloadOnQueryUpdate(query, payload);
              let res = await ApiService.getAppTableData(payload, {
                signal: abortController.signal,
              });
              if (latestRequest.current !== requestId) return;
              if (!res.errorObject && !query.export) {
                let tableData = generateRowID(res?.data?.data, false);
                setSource(tableData);
                setCount(res?.data?.count, false);
                setTableLoader(false);
                tableStoreRef.current = tableStore;
                setTableStore(tableStore);
                selectableRowsHook.setSelected(defaultRowID(res?.data?.count));
                setResReceived(true);
              } else if (query.export) {
                let resData = res?.data?.data;
                let columns = tableStore
                  .getState()
                  .columns.slice(1)
                  .filter((item) => item.prop !== "actions");
                const data = [
                  columns.map((item) => `"${item.name}"`).join(","),
                ];
                for (const item of resData) {
                  const rowData = columns.map((col) => {
                    const value = item[col.prop] ?? "";
                    return `"${value}"`;
                  });
                  data.push(rowData.join(","));
                }
                downloadAsCSVFile(data.join("\n"), tViews("applications"));
                delete query.export;
                setTableLoader(false);
              } else {
                setTableError(res.errorObject);
                setTableLoader(false);
              }
            },
          });
          useTableSettings(
            LOCAL_STORAGE_LABELS.AD_USAGE_APPLICATION_TABLE,
            AD_USAGE_APPLICATION_TABLE_DEFAULT_TGL,
            columnsHook,
            tableStore,
          );
        } catch (error) {
          // Handle error
          if (error.name === "AbortError") return; // Ignore abort error
          // Only process if this is the latest request
          if (latestRequest.current !== requestId) return;
          setTableError(error);
          setTableLoader(false);
        }
      }
    };

    runEffect();
  }, [props.circuit_name]);

  return (
    <div
      className={css["ad-app-table"]}
      aria-label={tViews("usageBasedAD.appTableHeader")}
    >
      <h2>{tViews("usageBasedAD.appTableHeader")}</h2>
      {tableStore && resReceived ? (
        <div>
          <div className="customt-timestamp">
            <CustomTimeStamp
              prevTime={props.selectedHour}
              currentTime={addUnitsFromDate(
                new Date(props.selectedHour),
                1,
                "h",
              )}
            />
          </div>
          <ADTableView
            tableStore={tableStore}
            header={tViews("applications")}
            columns={tableStore?.getState()?.columns}
            id={"ad-app-table"}
            status={{ isLoading: tableLoader, error: tableError }}
            defaultSort={defaultSort}
            getSelectedRows={getSelectedRows}
            isPagination={true}
            entity={tViews("applicationSmall")}
            tableSettings={{
              localStorageName: LOCAL_STORAGE_LABELS.AD_USAGE_APPLICATION_TABLE,
              defaultToggles: AD_USAGE_APPLICATION_TABLE_DEFAULT_TGL,
              dataTestid: tableSettingsId,
            }}
          />
        </div>
      ) : tableLoader ? (
        <Spinner />
      ) : (
        tableError && (
          <ErrorComponent
            code={"500"}
            width={"110px"}
            className={"large-dashlet-error"}
          />
        )
      )}
    </div>
  );
};

ApplicationsTable.propTypes = {
  circuit_name: PropTypes.string.isRequired,
  setAppUsgList: PropTypes.func,
  selectedHour: PropTypes.string,
};

export default ApplicationsTable;
